import { PortalReadState } from '../portal-read-state.tsx';
import { WorkPage, WorkSection } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminAffiliatesApi, AdminApiError, type AdminPartnerDetail } from '../../api/admin.ts';
import { Alert, Button, ConfirmDialog, LoadingBlock, Select, TextArea, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import {
  Pill,
  Td,
  Th,
  TableShell,
  money,
  partnerStatusTone,
  periodLabel,
  riskTone,
  shortDate,
} from './affiliate-common.tsx';

/**
 * One partner, and every decision an operator makes about them: the status
 * that gates their earning, the tax paperwork that gates their payout, manual
 * commission adjustments, and which customers count as theirs. Each action
 * demands a reason because the API's schemas do — the forms here simply
 * refuse to submit without one rather than letting the server say no.
 */

const STATUS_TARGETS = [
  { value: 'active', label: 'Active (approves a pending application)' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'payout_hold', label: 'Payout hold' },
  { value: 'compliance_review', label: 'Compliance review' },
  { value: 'rejected', label: 'Rejected (closes a pending application)' },
  { value: 'terminated', label: 'Terminated' },
];

const currentPeriod = () => new Date().toISOString().slice(0, 7);

export default function AdminPartnerDetailPage() {
  const { affiliateId = '' } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['admin', 'affiliates', 'partner', affiliateId],
    queryFn: ({ signal }) => adminAffiliatesApi.getPartner(affiliateId, signal),
    enabled: affiliateId.length > 0,
  });
  // The promotion plan lives on the application record, and it is exactly
  // what an operator needs in front of them when deciding a pending partner.
  // Asked for BY PARTNER: the unfiltered listing answers from a newest-100
  // window, in which an older pending applicant simply does not appear.
  const applications = useQuery({
    queryKey: ['admin', 'affiliates', 'applications', affiliateId],
    queryFn: ({ signal }) => adminAffiliatesApi.listApplications({ affiliateId }, signal),
    enabled: affiliateId.length > 0,
  });

  useDocumentTitle(detail.data ? detail.data.partner.displayName : 'Partner');

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'affiliates'] });
  }

  function report(caught: unknown, fallback: string) {
    setNotice(null);
    setError(caught instanceof AdminApiError ? caught.message : fallback);
  }

  if (detail.isLoading) return <PortalReadState title="Partner" label="Loading partner…" />;
  if (detail.isError || !detail.data) {
    return (
      <PortalReadState title="Partner" error="That partner could not be loaded." retry={() => detail.refetch()}>
        <BackLink />
      </PortalReadState>
    );
  }

  const { partner, customers, commissions, payableBalanceCents, notes, currency } = detail.data;
  const application = (applications.data?.applications ?? [])
    .filter((row) => row.affiliateId === partner.id)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];

  return (
    <div className="portal-page">
      <WorkPage title={partner.displayName}>
        <BackLink />
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Pill value={partner.status} tone={partnerStatusTone(partner.status)} />
          <Pill value={partner.riskState} tone={riskTone(partner.riskState)} />
          <code className="rounded bg-[color:var(--color-surface)] px-1.5 py-0.5 text-sm">/p/{partner.slug}</code>
        </div>

        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}
        {notice ? (
          <div className="mb-4">
            <Alert tone="success">{notice}</Alert>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label="Payable balance"
            value={money(payableBalanceCents, currency)}
            sub="Finalized, minus payouts"
          />
          <StatTile
            label="Attributed customers"
            value={String(detail.data.customersTotal)}
            sub={detail.data.customersTotal > customers.length ? `Newest ${customers.length} listed below` : undefined}
          />
          <StatTile
            label="Commission rate"
            value={partner.negotiatedRatePercent === null ? 'Tier' : `${partner.negotiatedRatePercent}%`}
            sub={partner.negotiatedRatePercent === null ? 'Follows the tier table' : 'Negotiated override'}
          />
          {/* Facts only, no Ready/Blocked verdict: whether a payout is held is
            decided at calculation time by rules that also weigh status, risk
            state and program configuration, and a verdict computed here from
            two of those inputs would sometimes say the opposite. The payout
            screen's per-partner hold reasons are the real answer. */}
          <StatTile
            label="Payout account"
            value={partner.paypalOnFile ? 'PayPal on file' : 'No PayPal'}
            sub={`Tax documentation ${partner.taxDocumentationOnFile ? 'on file' : 'not on file'} · holds are decided per payout`}
          />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <WorkSection title="Profile">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
              <ProfileRow label="Contact">{partner.contactName ?? '—'}</ProfileRow>
              <ProfileRow label="Email">{partner.contactEmail}</ProfileRow>
              <ProfileRow label="Phone">{partner.phone ?? '—'}</ProfileRow>
              <ProfileRow label="Business">{partner.businessName ?? '—'}</ProfileRow>
              <ProfileRow label="Website">
                {partner.website ? (
                  <a href={partner.website} target="_blank" rel="noreferrer" className="hover:underline">
                    {partner.website}
                  </a>
                ) : (
                  '—'
                )}
              </ProfileRow>
              <ProfileRow label="Address">
                {[partner.address.line1, partner.address.line2, partner.address.city, partner.address.state]
                  .filter(Boolean)
                  .join(', ') || '—'}
                {partner.address.postalCode ? ` ${partner.address.postalCode}` : ''}
              </ProfileRow>
              <ProfileRow label="Type">{partner.partnerType?.replace(/_/g, ' ') ?? '—'}</ProfileRow>
              <ProfileRow label="Joined">{shortDate(partner.createdAt)}</ProfileRow>
              <ProfileRow label="Approved">{shortDate(partner.approvedAt)}</ProfileRow>
            </dl>
            {application ? (
              <div className="mt-3 border-t border-[color:var(--color-line)] pt-3">
                <p className="text-sm font-medium">
                  Application ({application.status.replace(/_/g, ' ')}, {shortDate(application.createdAt)})
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-[color:var(--color-muted)]">
                  {application.promotionPlan}
                </p>
                {application.decisionReason ? (
                  <p className="mt-1 text-xs text-[color:var(--color-muted)]">Decision: {application.decisionReason}</p>
                ) : null}
              </div>
            ) : null}
          </WorkSection>

          <div className="space-y-6">
            <StatusCard
              partner={partner}
              applicationState={
                partner.status !== 'application_pending' || applications.isSuccess
                  ? 'ready'
                  : applications.isError
                    ? 'failed'
                    : 'loading'
              }
              onRetryApplications={() => void applications.refetch()}
              onDone={(status) => {
                setError(null);
                setNotice(`Status changed to ${status.replace(/_/g, ' ')}.`);
                void refresh();
              }}
              onError={(caught) => report(caught, 'The status could not be changed.')}
            />
            <TaxDocumentationCard
              partner={partner}
              onDone={(received) => {
                setError(null);
                setNotice(received ? 'Tax documentation recorded.' : 'Tax documentation cleared.');
                void refresh();
              }}
              onError={(caught) => report(caught, 'Tax documentation could not be updated.')}
            />
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <AdjustmentCard
            affiliateId={partner.id}
            currency={currency}
            onDone={() => {
              setError(null);
              setNotice('Adjustment written to the ledger.');
              void refresh();
            }}
            onError={(caught) => report(caught, 'The adjustment could not be written.')}
          />
          <AttachCustomerCard
            affiliateId={partner.id}
            displayName={partner.displayName}
            onDone={(action) => {
              setError(null);
              setNotice(
                action === 'attach'
                  ? 'Customer attributed to this partner. Commission already earned elsewhere is unchanged.'
                  : 'Attribution removed.',
              );
              void refresh();
            }}
            onError={(caught) => report(caught, 'The attribution change could not be made.')}
          />
        </div>

        <CustomersSection
          customers={customers}
          onDone={() => {
            setError(null);
            setNotice('Attribution removed.');
            void refresh();
          }}
          onError={(caught) => report(caught, 'The attribution could not be removed.')}
        />

        <section className="mt-6">
          <h2 className="mb-3 text-base font-semibold">Commission ledger</h2>
          {commissions.length >= 200 ? (
            <p className="mb-3 text-sm text-[color:var(--color-muted)]">
              Showing the newest 200 entries — the full ledger continues past what is listed here.
            </p>
          ) : null}
          {commissions.length === 0 ? (
            <p className="text-sm text-[color:var(--color-muted)]">Nothing earned yet.</p>
          ) : (
            <TableShell
              label="Commission ledger"
              head={
                <tr>
                  <Th>Period</Th>
                  <Th>Type</Th>
                  <Th className="text-right">Amount</Th>
                  <Th className="text-right">On revenue</Th>
                  <Th className="text-right">Rate</Th>
                  <Th>State</Th>
                  <Th>Reason</Th>
                  <Th>Written</Th>
                </tr>
              }
            >
              {commissions.map((entry) => (
                <tr key={entry.id} className="border-b border-[color:var(--color-line)] last:border-b-0">
                  <Td>{periodLabel(entry.period)}</Td>
                  <Td>{entry.type.replace(/_/g, ' ')}</Td>
                  <Td className="text-right font-medium">{money(entry.amountCents, entry.currency)}</Td>
                  <Td className="text-right text-[color:var(--color-muted)]">
                    {entry.grossCents === null ? '—' : money(entry.grossCents, entry.currency)}
                  </Td>
                  <Td className="text-right text-[color:var(--color-muted)]">
                    {entry.ratePercent === null ? '—' : `${entry.ratePercent}%`}
                  </Td>
                  <Td>
                    <Pill
                      value={entry.payoutId ? 'in payout' : entry.finalized ? 'finalized' : 'estimated'}
                      tone={entry.payoutId ? 'good' : entry.finalized ? 'neutral' : 'warn'}
                    />
                  </Td>
                  <Td className="max-w-64 table-text text-[color:var(--color-muted)]" title={entry.reason ?? undefined}>
                    {entry.reason ?? '—'}
                  </Td>
                  <Td className="text-[color:var(--color-muted)]">{shortDate(entry.createdAt)}</Td>
                </tr>
              ))}
            </TableShell>
          )}
        </section>

        <NotesSection
          affiliateId={partner.id}
          notes={notes}
          onDone={() => {
            setError(null);
            void refresh();
          }}
          onError={(caught) => report(caught, 'The note could not be added.')}
        />
      </WorkPage>
    </div>
  );
}

function BackLink() {
  return (
    <p className="mb-3 text-sm">
      <Link to="/admin/partners" className="text-[color:var(--color-brand)] hover:underline">
        ← All partners
      </Link>
    </p>
  );
}

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card">
      <p className="text-sm font-medium text-[color:var(--color-muted)]">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
      {sub ? <p className="mt-1 text-xs text-[color:var(--color-muted)]">{sub}</p> : null}
    </div>
  );
}

function ProfileRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-[color:var(--color-muted)]">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

function StatusCard({
  partner,
  applicationState,
  onRetryApplications,
  onDone,
  onError,
}: {
  partner: AdminPartnerDetail['partner'];
  /**
   * Whether the application record — the promotion plan a pending decision is
   * made FROM — is on screen. Always 'ready' for a partner past that stage;
   * while it is not, deciding is withheld rather than allowed blind.
   */
  applicationState: 'ready' | 'loading' | 'failed';
  onRetryApplications: () => void;
  onDone: (status: string) => void;
  onError: (caught: unknown) => void;
}) {
  const [status, setStatus] = useState(partner.status === 'application_pending' ? 'active' : partner.status);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await adminAffiliatesApi.setPartnerStatus(partner.id, status, reason.trim());
      setReason('');
      onDone(status);
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <WorkSection title="Status">
      {partner.status === 'application_pending' ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">
          This application is waiting on a decision — activating approves it, rejecting closes it.
        </p>
      ) : null}
      {applicationState === 'loading' ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">Loading the application&hellip;</p>
      ) : null}
      {applicationState === 'failed' ? (
        <div className="mb-3">
          <Alert tone="warning" title="The application could not be loaded">
            Its promotion plan is what this decision is made from, so deciding is disabled until it is on screen.{' '}
            <Button variant="ghost" onClick={onRetryApplications}>
              Try again
            </Button>
          </Alert>
        </div>
      ) : null}
      <form onSubmit={submit} noValidate>
        <Select
          label="Move to"
          // A pending applicant gets application OUTCOMES only. Payout hold
          // and compliance review are earning statuses, so offering them here
          // would let an operator start an unapproved applicant's commission
          // clock while the application still sits in the decision queue.
          options={
            partner.status === 'application_pending'
              ? STATUS_TARGETS.filter((target) => target.value === 'active' || target.value === 'rejected')
              : STATUS_TARGETS
          }
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        />
        <TextInput
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Recorded in the audit trail"
        />
        <Button
          type="submit"
          variant={status === 'active' ? 'primary' : 'danger'}
          busy={busy}
          disabled={!reason.trim() || status === partner.status || applicationState !== 'ready'}
        >
          Apply
        </Button>
      </form>
    </WorkSection>
  );
}

function TaxDocumentationCard({
  partner,
  onDone,
  onError,
}: {
  partner: AdminPartnerDetail['partner'];
  onDone: (received: boolean) => void;
  onError: (caught: unknown) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const received = partner.taxDocumentationOnFile;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await adminAffiliatesApi.setTaxDocumentation(partner.id, !received, reason.trim());
      setReason('');
      onDone(!received);
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <WorkSection title="Tax documentation">
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">
        {received
          ? 'On file. Clearing it holds every future payout until it is recorded again.'
          : 'Not on file — payouts to this partner are held until somebody records the paperwork.'}
      </p>
      <form onSubmit={submit} noValidate>
        <TextInput
          label={received ? 'Why it no longer counts' : 'What you verified'}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={received ? 'e.g. W-9 was for a dissolved entity' : 'e.g. Signed W-9 received 2026-08-20'}
        />
        <Button type="submit" variant={received ? 'danger' : 'primary'} busy={busy} disabled={!reason.trim()}>
          {received ? 'Clear tax documentation' : 'Record as received'}
        </Button>
      </form>
    </WorkSection>
  );
}

function AdjustmentCard({
  affiliateId,
  currency,
  onDone,
  onError,
}: {
  affiliateId: string;
  currency: string;
  onDone: () => void;
  onError: (caught: unknown) => void;
}) {
  const [entryType, setEntryType] = useState('admin_adjustment');
  const [amount, setAmount] = useState('');
  const [period, setPeriod] = useState(currentPeriod());
  const [reason, setReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // Whole cents only, refused rather than rounded: "-1.005" through
  // Math.round would write a different signed amount than the operator
  // typed, and a financial correction that is not the number entered is
  // worse than one that is rejected.
  const wholeCents = /^-?\d+(\.\d{1,2})?$/.test(amount.trim());
  const amountCents = wholeCents ? Math.round(Number.parseFloat(amount) * 100) : Number.NaN;
  // A real calendar month: "2026-13" fit the old shape and wrote a ledger
  // entry no payout could ever include, because only real months are run.
  const valid =
    Number.isFinite(amountCents) &&
    amountCents !== 0 &&
    /^\d{4}-(0[1-9]|1[0-2])$/.test(period) &&
    reason.trim().length > 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await adminAffiliatesApi.createAdjustment({
        affiliateId,
        amountCents,
        entryType: entryType as 'admin_adjustment' | 'bonus' | 'correction',
        period,
        reason: reason.trim(),
        ...(internalNotes.trim() ? { internalNotes: internalNotes.trim() } : {}),
      });
      setAmount('');
      setReason('');
      setInternalNotes('');
      onDone();
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <WorkSection title="Commission adjustment">
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">
        A new ledger entry, never an edit of an old one. Enter a negative amount for a clawback.
      </p>
      <form onSubmit={submit} noValidate>
        <div className="flex flex-wrap gap-3">
          <div className="w-44">
            <Select
              label="Type"
              options={[
                { value: 'admin_adjustment', label: 'Adjustment' },
                { value: 'bonus', label: 'Bonus' },
                { value: 'correction', label: 'Correction' },
              ]}
              value={entryType}
              onChange={(event) => setEntryType(event.target.value)}
            />
          </div>
          <div className="w-36">
            <TextInput
              label={`Amount (${currency})`}
              type="number"
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="-25.00"
            />
          </div>
          <div className="w-36">
            <TextInput
              label="Period"
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
              placeholder="YYYY-MM"
            />
          </div>
        </div>
        <TextInput
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Shown to the partner alongside the entry"
        />
        <TextArea
          label="Internal notes (optional)"
          rows={2}
          value={internalNotes}
          onChange={(event) => setInternalNotes(event.target.value)}
        />
        <Button type="submit" variant="primary" busy={busy} disabled={!valid}>
          Write to ledger
        </Button>
      </form>
    </WorkSection>
  );
}

function AttachCustomerCard({
  affiliateId,
  displayName,
  onDone,
  onError,
}: {
  affiliateId: string;
  displayName: string;
  onDone: (action: 'attach' | 'remove') => void;
  onError: (caught: unknown) => void;
}) {
  const [companyId, setCompanyId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'attach' | 'remove' | null>(null);

  async function run(action: 'attach' | 'remove') {
    setBusy(action);
    try {
      if (action === 'attach') {
        await adminAffiliatesApi.overrideAttribution({
          companyId: companyId.trim(),
          affiliateId,
          reason: reason.trim(),
        });
      } else {
        await adminAffiliatesApi.removeAttribution({ companyId: companyId.trim(), reason: reason.trim() });
      }
      setCompanyId('');
      setReason('');
      onDone(action);
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(null);
    }
  }

  return (
    <WorkSection title="Attribute a customer">
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">
        Attaches a company to {displayName}, replacing any existing attribution — or removes whatever attribution a
        company has. Removal works by id for any customer, including ones past the listing below. Commission already
        earned stays where it is — move it with adjustments if it should follow.
      </p>
      <form onSubmit={(event) => event.preventDefault()} noValidate>
        <TextInput
          label="Company id"
          value={companyId}
          onChange={(event) => setCompanyId(event.target.value)}
          hint="From the company's page in this console"
        />
        <TextInput label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="primary"
            busy={busy === 'attach'}
            disabled={busy !== null || !companyId.trim() || !reason.trim()}
            onClick={() => void run('attach')}
          >
            Attribute customer
          </Button>
          <Button
            type="button"
            variant="ghost"
            busy={busy === 'remove'}
            disabled={busy !== null || !companyId.trim() || !reason.trim()}
            onClick={() => void run('remove')}
          >
            Remove attribution
          </Button>
        </div>
      </form>
    </WorkSection>
  );
}

function CustomersSection({
  customers,
  onDone,
  onError,
}: {
  customers: AdminPartnerDetail['customers'];
  onDone: () => void;
  onError: (caught: unknown) => void;
}) {
  const [removing, setRemoving] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  async function confirmRemove() {
    if (!removing) return;
    setBusy(true);
    setDialogError(null);
    try {
      await adminAffiliatesApi.removeAttribution({ companyId: removing, reason: reason.trim() });
      setRemoving(null);
      setReason('');
      onDone();
    } catch (caught) {
      setDialogError(caught instanceof AdminApiError ? caught.message : 'The attribution could not be removed.');
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-6">
      <h2 className="mb-3 text-base font-semibold">Attributed customers</h2>
      {customers.length === 0 ? (
        <p className="text-sm text-[color:var(--color-muted)]">No customers are attributed to this partner.</p>
      ) : (
        <TableShell
          label="Attributed customers"
          head={
            <tr>
              <Th>Company</Th>
              <Th>Attributed</Th>
              <Th>Source</Th>
              <Th>Campaign</Th>
              <Th />
            </tr>
          }
        >
          {customers.map((customer) => (
            <tr key={customer.companyId} className="border-b border-[color:var(--color-line)] last:border-b-0">
              <Td>
                <Link
                  to={`/admin/companies/${customer.companyId}`}
                  className="font-medium text-[color:var(--color-brand)] hover:underline"
                >
                  {customer.companyId}
                </Link>
              </Td>
              <Td className="text-[color:var(--color-muted)]">{shortDate(customer.attributedAt)}</Td>
              <Td>{customer.source?.replace(/_/g, ' ') ?? '—'}</Td>
              <Td>{customer.campaign ?? '—'}</Td>
              <Td className="text-right">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setRemoving(customer.companyId);
                    setReason('');
                    setDialogError(null);
                  }}
                >
                  Remove
                </Button>
              </Td>
            </tr>
          ))}
        </TableShell>
      )}

      {removing ? (
        <ConfirmDialog
          title="Remove this attribution?"
          confirmLabel="Remove attribution"
          cancelLabel="Keep it"
          confirmVariant="danger"
          confirmDisabled={reason.trim().length === 0}
          busy={busy}
          error={dialogError}
          onConfirm={() => void confirmRemove()}
          onCancel={() => setRemoving(null)}
        >
          <p className="mb-3 text-sm">
            Company <code>{removing}</code> stops counting toward this partner. Future revenue from it earns nothing;
            commission already earned is untouched.
          </p>
          <TextInput label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} autoFocus />
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

function NotesSection({
  affiliateId,
  notes,
  onDone,
  onError,
}: {
  affiliateId: string;
  notes: AdminPartnerDetail['notes'];
  onDone: () => void;
  onError: (caught: unknown) => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await adminAffiliatesApi.addPartnerNote(affiliateId, note.trim());
      setNote('');
      onDone();
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-6 card">
      <h2 className="mb-3 text-base font-semibold">Notes</h2>
      <form onSubmit={submit} className="mb-4 flex flex-wrap items-end gap-3" noValidate>
        <div className="min-w-64 flex-1">
          <TextInput
            label="Add an internal note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            hint="Visible to operators only, never to the partner."
          />
        </div>
        <div className="pb-4">
          <Button type="submit" busy={busy} disabled={!note.trim()}>
            Add note
          </Button>
        </div>
      </form>
      {notes.length === 0 ? (
        <p className="text-sm text-[color:var(--color-muted)]">No notes yet.</p>
      ) : (
        <ul className="space-y-3">
          {notes.map((entry) => (
            <li key={entry.id} className="border-b border-[color:var(--color-line)] pb-3 text-sm last:border-b-0">
              <p className="whitespace-pre-wrap">{entry.note}</p>
              <p className="mt-1 text-xs text-[color:var(--color-muted)]">
                {entry.author} · {shortDate(entry.createdAt)}
                {entry.isPublic ? ' · visible to partner' : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
